import React, { useState } from 'react';
import { useDispatch } from "react-redux";
import { setCategories } from '../reducers/CategoriesSlice';
import useApi from "../hooks/useApi";
import { getCategories } from "../api/FlaskAPI";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    Box,
    CircularProgress
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DatePicker } from 'rsuite';
import axios from '../api/axios_client';
import { url } from '../api/axios_client';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';


const TiffUploadArea = ({ onFileChange, onDateChange, onDelete, uploadId, file, date }) => {

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            p: 2,
            border: '1px dashed #ccc',
            borderRadius: 1
        }}>
            <Typography noWrap style={{ flex: 1 }}>
                {file?.name || 'No file selected'}
            </Typography>
            <DatePicker
                disabled={!file}
                format="dd/MM/yyyy"
                value={date}
                onChange={(date) => onDateChange(uploadId, date)}
                style={{ width: 200 }}
                container={document.body}
                placement="autoVerticalStart"
                preventOverflow
            />
            <IconButton onClick={() => onDelete(uploadId)} color="error">
                <DeleteOutlineIcon />
            </IconButton>
        </Box>
    );
};

const AddTiffDialog = ({ open, onClose, index }) => {
    const [uploads, setUploads] = useState([{ id: Date.now(), file: null, date: null }]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [failedTiffs, setFailedTiffs] = useState([]);
    const { t } = useTranslation();

    const handleFileInputChange = (event) => {
        const files = Array.from(event.target.files);
        const newUploads = files.map((file, idx) => ({
            id: Date.now() + idx,
            file: file,
            date: null
        }));

        // If there are existing uploads with files, append new ones
        if (uploads.some(upload => upload.file !== null)) {
            setUploads([...uploads, ...newUploads]);
        } else {
            // Otherwise, replace the empty uploads with new ones
            setUploads(newUploads);
        }
    };

    const handleAddUpload = () => {
        setUploads([...uploads, { id: Date.now(), file: null, date: null }]);
    };

    const handleDateChange = (uploadId, date) => {
        setUploads(uploads.map(upload =>
            upload.id === uploadId ? { ...upload, date: date } : upload
        ));
    };

    const handleDeleteUpload = (uploadId) => {
        const newUploads = uploads.filter((upload) => upload.id !== uploadId);
        if (newUploads.length === 0) {
            // Keep at least one empty upload slot
            setUploads([{ id: Date.now(), file: null, date: null }]);
        } else {
            setUploads(newUploads);
        }
    };

    const dispatch = useDispatch(); // Add this at the top with other imports
    const categoriesFetch = useApi(getCategories); // Add this at the top

    const handleSubmit = async () => {
        let toastId;
        try {
            // Clear any previous errors
            setError(null);
            setFailedTiffs([]); // Clear previous failed tiffs

            // Validate inputs
            const invalidUploads = uploads.some(upload => !upload.file || !upload.date);
            if (invalidUploads) {
                toast.error(t('Tiff.provideBothFileAndDate'));
                return;
            }

            // CHECK FOR DUPLICATE DATES
            const dates = uploads.map(upload =>
                new Date(upload.date.setHours(12, 0, 0, 0)).toISOString().split('T')[0]
            );
            const uniqueDates = new Set(dates);
            if (uniqueDates.size !== dates.length) {
                toast.error(t('Tiff.duplicateDatesNotAllowed'));
                return;
            }

            setUploading(true);
            toastId = toast.loading(t('Tiff.uploadingFiles'));

            // Create FormData and append files with dates
            const formData = new FormData();
            uploads.forEach((upload, index) => {
                formData.append(`file${index}`, upload.file);
                formData.append(`date${index}`, new Date(upload.date.setHours(12)).toISOString().split('T')[0]);
            });

            console.log(formData)

            // Send request
            const response = await axios.post(
                `${url}/indexes/${index.id}/tifs`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.status === 201) {
                // Check for failed_tifs in response
                const failedTifsList = response.data?.failed_tifs || [];

                // stop loading before doing anything else
                setUploading(false);

                if (failedTifsList.length > 0) {
                    setFailedTiffs(failedTifsList);
                    toast.update(toastId, {
                        render: t('Tiff.uploadCompleteWithErrors'),
                        type: "warning",
                        isLoading: false,
                        autoClose: 5000
                    });
                } else {
                    toast.update(toastId, {
                        render: t('Tiff.uploadSuccess'),
                        type: "success",
                        isLoading: false,
                        autoClose: 3000
                    });
                    onClose();
                    setUploads([{ id: Date.now(), file: null, date: null }]);
                }

                // Update categories
                try {
                    localStorage.removeItem('categories');
                    const categoriesResponse = await categoriesFetch.request({
                        index: "ndvi"
                    }, false);
                    console.log("categories response:", categoriesResponse);
                    dispatch(setCategories(categoriesResponse.data));
                    localStorage.setItem('categories', JSON.stringify(categoriesResponse.data));
                } catch (categoriesError) {
                    console.error('Error refreshing categories:', categoriesError);
                    // Don't show error to user - upload was successful
                }

                return; // early exit
            }
        } catch (error) {
            console.error('Upload error:', error);

            // Show backend error message if available, otherwise show generic message
            const errorMessage = error.response?.data?.message || "Failed to upload TIFF files";

            // Set error state to display in dialog
            setError(errorMessage);

            toast.update(toastId, {
                render: errorMessage,
                type: "error",
                isLoading: false,
                autoClose: 3000
            });
        } finally {
            // This handles error cases
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="md" fullWidth
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'flex-start',
                    paddingTop: '10vh'
                },
                '& .MuiDialog-paper': {
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    margin: 0
                }
            }}>
            <DialogTitle>{t('Tiff.uploadTiffFiles')}</DialogTitle>
            <DialogContent sx={{ overflowY: 'auto' }}>
                {error && (
                    <Typography
                        color="error"
                        sx={{
                            mb: 2,
                            p: 2,
                            backgroundColor: '#ffebee',
                            borderRadius: 1,
                            border: '1px solid #ffcdd2'
                        }}
                    >
                        {error}
                    </Typography>
                )}

                {/* Failed TIFFs List */}
                {failedTiffs.length > 0 && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 2,
                            backgroundColor: '#fff3e0',
                            borderRadius: 1,
                            border: '1px solid #ffcc80'
                        }}
                    >
                        <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                            {t('Tiff.failedTiffs')}:
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                            {failedTiffs.map((tif, index) => (
                                <li key={index}>
                                    <Typography variant="body2" color="text.secondary">
                                        {tif}
                                    </Typography>
                                </li>
                            ))}
                        </Box>
                    </Box>
                )}
                <Box sx={{ mb: 2, mt: 1 }}>
                    <input
                        type="file"
                        accept=".tif,.tiff"
                        onChange={handleFileInputChange}
                        multiple
                        style={{ display: 'none' }}
                        id="tiff-file-input"
                    />
                    <label htmlFor="tiff-file-input">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<AddCircleOutlineIcon />}
                        >
                            {t('Tiff.selectTiffFiles')}
                        </Button>
                    </label>
                </Box>

                {uploads.map((upload) => (
                    <TiffUploadArea
                        key={upload.id}
                        uploadId={upload.id}
                        file={upload.file}
                        date={upload.date}
                        onDateChange={handleDateChange}
                        onDelete={handleDeleteUpload}
                    />
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={uploading}>{t('Tiff.cancel')}</Button>
                <Button
                    disabled={uploading}
                    onClick={handleSubmit}
                    variant="contained"
                    startIcon={uploading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
                >
                    {uploading ? t('Tiff.uploading') : t('Tiff.upload')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddTiffDialog;
